import { useState, useEffect, useCallback, useMemo } from 'react';
import { userAPI, itemsAPI } from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Loader2, Package, Plus, Search, UserMinus, ArrowRightLeft } from 'lucide-react';

export function ProductHierarchyTab({ allUsers = [], onChanged }) {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPmId, setSelectedPmId] = useState('');
  const [portfolio, setPortfolio] = useState([]);
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  const [noSkuCount, setNoSkuCount] = useState(0);
  const [skuQuery, setSkuQuery] = useState('');
  const [checked, setChecked] = useState(new Set());
  const [targetExecId, setTargetExecId] = useState('');
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addUserId, setAddUserId] = useState('');

  const fetchManagers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await userAPI.productHierarchy();
      setManagers(res.data.managers || []);
      setSelectedPmId((prev) => prev || (res.data.managers || [])[0]?.id || '');
    } catch {
      toast.error('Failed to load product hierarchy');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchManagers(); }, [fetchManagers]);

  const selectedPm = useMemo(() => managers.find((m) => m.id === selectedPmId), [managers, selectedPmId]);

  const fetchPortfolio = useCallback(async () => {
    if (!selectedPmId) { setPortfolio([]); return; }
    setPortfolioLoading(true);
    try {
      const res = await itemsAPI.getAssignedTo(selectedPmId);
      const items = res.data.items || [];
      // Items without a SKU cannot be sub-assigned (SKU is the assignment key in Zoho)
      setPortfolio(items.filter((i) => i.sku));
      setNoSkuCount(items.length - items.filter((i) => i.sku).length);
    } catch {
      toast.error('Failed to load portfolio');
    } finally {
      setPortfolioLoading(false);
    }
  }, [selectedPmId]);

  useEffect(() => { setChecked(new Set()); setTargetExecId(''); fetchPortfolio(); }, [fetchPortfolio]);

  const filtered = useMemo(() => {
    const q = skuQuery.trim().toLowerCase();
    if (!q) return portfolio;
    return portfolio.filter((i) => `${i.sku} ${i.name}`.toLowerCase().includes(q));
  }, [portfolio, skuQuery]);

  const toggleSku = (sku) => setChecked((prev) => {
    const next = new Set(prev);
    next.has(sku) ? next.delete(sku) : next.add(sku);
    return next;
  });

  const handleAssign = async (mode) => {
    if (!checked.size) return;
    if (mode === 'add' && !targetExecId) { toast.error('Pick a product executive first'); return; }
    setSaving(true);
    try {
      const res = await userAPI.assignProductSkus({
        user_id: mode === 'add' ? targetExecId : (checked.size && selectedPmId),
        skus: Array.from(checked),
        mode,
      });
      toast.success(res.data.message);
      setChecked(new Set());
      await Promise.all([fetchPortfolio(), fetchManagers()]);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Assignment failed');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveSubAssignment = async () => {
    if (!checked.size) return;
    setSaving(true);
    try {
      // Sub-assignment is cleared by re-pointing the SKUs at the PM themselves
      const res = await userAPI.assignProductSkus({ user_id: selectedPmId, skus: Array.from(checked), mode: 'add' });
      toast.success(res.data.message);
      setChecked(new Set());
      await Promise.all([fetchPortfolio(), fetchManagers()]);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clear sub-assignment');
    } finally {
      setSaving(false);
    }
  };

  const handleAddExecutive = async () => {
    if (!addUserId || !selectedPmId) return;
    setSaving(true);
    try {
      await userAPI.updateHierarchy(addUserId, { hierarchy_type: 'product', reporting_to_user_id: selectedPmId });
      toast.success('Product executive added');
      setAddOpen(false);
      setAddUserId('');
      await fetchManagers();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add executive');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveExecutive = async (execId) => {
    setSaving(true);
    try {
      await userAPI.updateHierarchy(execId, { hierarchy_type: 'sales_purchase' });
      toast.success('Moved back to the Sales/Purchase hierarchy');
      await fetchManagers();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to move user');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  if (managers.length === 0) {
    return (
      <Card className="rounded-sm">
        <CardContent className="p-8 text-center space-y-2">
          <Package className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            No Product Managers yet. On the User List tab, give a user the <strong>Product Manager</strong> profile —
            they will appear here with their SKU portfolio.
          </p>
        </CardContent>
      </Card>
    );
  }

  const availableToAdd = allUsers.filter(
    (u) => u.id !== selectedPmId && u.hierarchy_type !== 'product' && u.is_active
  );

  return (
    <div className="grid gap-4 lg:grid-cols-[320px_1fr]" data-testid="product-hierarchy-panel">
      {/* Product Managers + executives */}
      <Card className="rounded-sm h-fit">
        <CardContent className="p-4 space-y-3">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Product Managers</Label>
          <div className="space-y-1">
            {managers.map((m) => (
              <button
                key={m.id}
                onClick={() => setSelectedPmId(m.id)}
                className={`w-full text-left px-3 py-2 rounded-sm transition-colors ${
                  m.id === selectedPmId ? 'bg-primary/10 border border-primary/30' : 'hover:bg-muted border border-transparent'
                }`}
                data-testid={`pm-select-${m.email}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">{m.name}</span>
                  <Badge variant="outline" className="text-[10px] rounded-sm font-normal">{m.owned_skus} SKUs</Badge>
                </div>
                <div className="text-xs text-muted-foreground">{m.crm_role_name || m.profile_name}</div>
                <div className="text-xs text-muted-foreground">{m.executives.length} executive{m.executives.length === 1 ? '' : 's'}</div>
              </button>
            ))}
          </div>

          {selectedPm && (
            <div className="pt-3 border-t space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  {selectedPm.name}'s Executives
                </Label>
                <Button size="sm" variant="outline" className="h-7 rounded-sm" onClick={() => setAddOpen(true)} data-testid="add-product-executive-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add
                </Button>
              </div>
              {selectedPm.executives.length === 0 ? (
                <p className="text-xs text-muted-foreground">No executives under this PM yet.</p>
              ) : (
                selectedPm.executives.map((e) => (
                  <div key={e.id} className="flex items-center gap-2 text-sm py-1" data-testid={`product-exec-${e.email}`}>
                    <Package className="h-3 w-3 text-amber-600" />
                    <span className="truncate">{e.name}</span>
                    <Badge variant="outline" className="text-[10px] rounded-sm font-normal">{e.sub_assigned_skus} SKUs</Badge>
                    <button
                      onClick={() => handleRemoveExecutive(e.id)}
                      className="ml-auto text-muted-foreground hover:text-destructive"
                      title="Move back to Sales/Purchase hierarchy"
                      data-testid={`remove-product-exec-${e.email}`}
                    >
                      <UserMinus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Portfolio + sub-assignment */}
      <Card className="rounded-sm">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={skuQuery}
                onChange={(e) => setSkuQuery(e.target.value)}
                placeholder="Search SKU or item name…"
                className="pl-8 rounded-sm"
                data-testid="portfolio-search"
              />
            </div>
            <span className="text-xs text-muted-foreground" data-testid="portfolio-count">
              {filtered.length} of {portfolio.length} SKUs in {selectedPm?.name || '—'}'s portfolio
              {noSkuCount > 0 && ` · ${noSkuCount} item(s) hidden (no SKU in Zoho)`}
            </span>
          </div>

          {checked.size > 0 && (
            <div className="flex flex-wrap items-center gap-2 p-3 bg-muted rounded-sm" data-testid="portfolio-actions">
              <span className="text-sm">{checked.size} selected</span>
              <Select value={targetExecId} onValueChange={setTargetExecId}>
                <SelectTrigger className="w-[220px] h-9 rounded-sm" data-testid="sub-assign-exec-select">
                  <SelectValue placeholder="Sub-assign to executive…" />
                </SelectTrigger>
                <SelectContent>
                  {(selectedPm?.executives || []).map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" className="rounded-sm" disabled={saving || !targetExecId} onClick={() => handleAssign('add')} data-testid="sub-assign-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ArrowRightLeft className="h-4 w-4 mr-1" />}
                Sub-assign
              </Button>
              <Button size="sm" variant="outline" className="rounded-sm" disabled={saving} onClick={handleRemoveSubAssignment} data-testid="clear-sub-assign-btn">
                Keep with PM only
              </Button>
            </div>
          )}

          {portfolioLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-10 text-center">
              No SKUs assigned to this Product Manager yet. Assign items from the Items page.
            </p>
          ) : (
            <ScrollArea className="h-[460px] pr-3">
              <div className="space-y-0.5">
                {filtered.map((item) => (
                  <label
                    key={item.sku}
                    className="flex items-center gap-3 py-1.5 px-2 rounded-sm hover:bg-muted/60 cursor-pointer"
                    data-testid={`portfolio-row-${item.sku}`}
                  >
                    <Checkbox checked={checked.has(item.sku)} onCheckedChange={() => toggleSku(item.sku)} />
                    <span className="text-sm font-mono shrink-0">{item.sku}</span>
                    <span className="text-xs text-muted-foreground truncate">{item.name}</span>
                    {item.product_executive_name && (
                      <Badge variant="outline" className="ml-auto text-[10px] rounded-sm font-normal bg-amber-50 text-amber-800 border-amber-200 shrink-0">
                        {item.product_executive_name}
                      </Badge>
                    )}
                  </label>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Product Executive</DialogTitle>
            <DialogDescription>
              The user moves into the Product hierarchy under {selectedPm?.name} and will only see data for the SKUs
              sub-assigned to them.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>User</Label>
            <Select value={addUserId} onValueChange={setAddUserId}>
              <SelectTrigger className="rounded-sm" data-testid="add-exec-user-select">
                <SelectValue placeholder="Select a user…" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {availableToAdd.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name} — {u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button className="rounded-sm" disabled={!addUserId || saving} onClick={handleAddExecutive} data-testid="confirm-add-exec-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
